import { defineAbility } from '@casl/ability'
import Statsig, { StatsigOptions } from 'statsig-js'
import { Module, ModulePerms, SubModulePerms } from '../../domain/module'
import { getRolePermissionsUseCase } from '../../application/get-role-permissions.use-case'
import { odinRoleRepository } from '../odin-role.repository'
import { RolePermissions } from '../../domain/role'
import { getModulesUseCase } from '../../application/get-modules.use-case'
import { odinModuleRepository } from '../odin-module.repository'

type EnvTierType = 'production' | 'staging' | 'development'

const initializeStatsig = async (userAuth: any) => {
  const userStatsing = {
    userID: `${userAuth.id}`,
    email: userAuth.email,
    custom: {
      clientId: `${userAuth.clientId}`,
      clientName: userAuth.client.name,
      role: userAuth.role.name
    }
  }
  const optionStatsing: StatsigOptions = {
    environment: { tier: `${process.env.REACT_APP_ENV}` as EnvTierType }
  }
  await Statsig.initialize(process.env.REACT_APP_KEY_STACKSING as string, userStatsing, optionStatsing)
}
const permissionGroups = {
  withInstaleapEmail: [SubModulePerms.USE_USER_CHANGE_CLIENT, SubModulePerms.PAR_GENERAL_SEE],
  onlyForInternalAdmin: [
    SubModulePerms.RES_RESOURCE_SWITCH,
    SubModulePerms.PAR_LOGISTIC_SEE,
    SubModulePerms.PAR_REASON_SEE,
    SubModulePerms.USE_USER_GET_CLIENTS,
    ModulePerms.SEG,
    ModulePerms.ORA,
    SubModulePerms.MET_CAPACITY_SLOT_SWITCH,
    SubModulePerms.RES_STORE_MANAGER,
    SubModulePerms.USE_ROLE_SEE,
    SubModulePerms.USE_ROLE_CREATE,
    SubModulePerms.USE_ROLE_UPDATE
  ],
  withoutValidation: [
    ModulePerms.ANA,
    ModulePerms.COM,
    ModulePerms.WMS,
    ModulePerms.LOG,
    ModulePerms.SST,
    SubModulePerms.RES_FLEET_MANAGER,
    SubModulePerms.USE_USER_CREATE,
    SubModulePerms.USE_USER_UPDATE,
    SubModulePerms.USE_GROUP_SEE,
    SubModulePerms.USE_GROUP_CREATE,
    SubModulePerms.USE_GROUP_UPDATE,
    SubModulePerms.RES_RESOURCE_SEE,
    SubModulePerms.RES_RESOURCE_CREATE,
    SubModulePerms.RES_RESOURCE_UPDATE
  ]
}

export const AccessControl = {
  abilityInstance: null,
  currentRole: '',
  isInstaleapEmail: false,
  modules: [],
  defineAbilityForRole: async function (rules: string[], userAuth: any) {
    await initializeStatsig(userAuth)
    let newRolePermissions: RolePermissions = { permissions: [] }
    if (userAuth.avt_role_id && !userAuth.avt_role?.super_admin) {
      newRolePermissions = await getRolePermissionsUseCase(odinRoleRepository)(userAuth.avt_role_id)
    }
    this.modules = await getModulesUseCase(odinModuleRepository)()

    this.currentRole = userAuth.avt_role?.name
    this.isInstaleapEmail = userAuth.email.endsWith('@instaleap.io')

    if (this.abilityInstance) return this.abilityInstance
    this.abilityInstance = defineAbility((can, cannot) => {
      if (userAuth.avt_role?.super_admin) {
        can('manage', 'all')
        cannot(ModulePerms.CAL, userAuth.avt_role?.name)
        cannot(SubModulePerms.FLA_GENERAL_AUTOMATIC_ASSIGN, userAuth.avt_role?.name)
      } else {
        newRolePermissions.permissions?.map((permission) => can(permission, userAuth.avt_role?.name))
      }
    })
    return newRolePermissions
  },
  getAbility: function () {
    return this.abilityInstance
  },
  getRole: function () {
    return this.currentRole
  },
  validatePermission: function (permissionId: string) {
    if (!this.abilityInstance) return false
    if (Object.values(ModulePerms).includes(permissionId as any)) {
      const module = this.modules.find((module: Module) => module.id === permissionId)
      return this.abilityInstance.can(permissionId, this.currentRole) && module?.activeForClient
    }
    if (permissionGroups.withInstaleapEmail.includes(permissionId as any)) {
      return this.abilityInstance.can(permissionId, this.currentRole) && this.isInstaleapEmail
    }
    return this.abilityInstance.can(permissionId, this.currentRole)
  },
  isValidHierarchy: function (userHierarchy: number, targetHierarchy: number) {
    return userHierarchy < targetHierarchy
  }
}

export const Can = (allowedIds: string[], hierarchyContext?: HierarchyContextType): boolean => {
  const isGranted: boolean = allowedIds.some((permissionId) => AccessControl.validatePermission(permissionId))
  const isHierarchyFlagActive = hierarchyContext?.isFlagActive
  if (
    hierarchyContext === undefined ||
    isHierarchyFlagActive === false ||
    (isHierarchyFlagActive === true && hierarchyContext?.userIsSuperAdmin === true)
  )
    return isGranted

  const { userHierarchy, targetHierarchy, targetIsSuperAdmin, userIsSuperAdmin } = hierarchyContext
  if (userIsSuperAdmin && !targetIsSuperAdmin) return false
  return isGranted && AccessControl.isValidHierarchy(userHierarchy, targetHierarchy)
}

type HierarchyContextType = {
  isFlagActive: boolean
  userIsSuperAdmin: boolean
  targetIsSuperAdmin: boolean
  userHierarchy: number
  targetHierarchy: number
}
